/**
 * Helpers for translating disconnect mutation errors into user-facing copy.
 *
 * Every failure — 4xx validation failures, 5xx system failures, network
 * errors, request timeouts — collapses to written, non-backend copy.
 * Callers that need to detect a specific 4xx condition (e.g. the "set a
 * password first" case) do so via isPasswordSetupRequiredError, which
 * inspects the raw error separately and drives its own UI affordance.
 *
 * Used by GmailIntegration, CalendarIntegration, and MeetIntegration so all
 * three surfaces handle disconnect failures consistently.
 */

import { isApiError } from '@/lib/errors';

const GENERIC_DISCONNECT_MESSAGE =
  'Please try again, or contact support if this keeps happening.';

export const DISCONNECT_ERROR_TITLE = "We couldn't disconnect this account";

/**
 * Returns the user-facing description for a disconnect failure.
 * Pair with DISCONNECT_ERROR_TITLE for the heading/toast title.
 */
export function getDisconnectErrorMessage(_error: unknown): string {
  return GENERIC_DISCONNECT_MESSAGE;
}

/**
 * Detects the "Cannot disconnect the last login method. Please set a password
 * first or connect another account." 400 returned by `apps/users/views.py`
 * when the social account being disconnected is the user's only login method.
 *
 * The frontend uses this to surface a Set Password affordance inline rather
 * than leaving the user stuck. Pattern-matched against the user-facing copy
 * because the backend doesn't currently return a stable error code; if/when
 * the backend adds one, switch this to read it.
 */
export function isPasswordSetupRequiredError(error: unknown): boolean {
  if (!isApiError(error) || error.isServerError() || !error.message) {
    return false;
  }
  return /set a password/i.test(error.message);
}
